/**
 * PoolSelector Component
 *
 * Dropdown selector for pool management.
 * Part of the three-level selection system (Provider -> Pool -> Model).
 */

import React, { useState, useCallback } from 'react';
import { clsx } from 'clsx';
import { StatusBadge } from '../shared/StatusBadge';
import { ConfirmDialog } from '../../ui/ConfirmDialog';

export interface PoolOption {
  poolIndex: number;
  apiKeyPreview: string;
  hasApiKey: boolean;
  modelCount: number;
  weight: number;
  keySelectionMode: 'polling' | 'random';
}

export interface PoolSelectorProps {
  pools: PoolOption[];
  selectedPool: number | null;
  onSelect: (poolIndex: number) => void;
  onAdd: () => void;
  onDelete: (poolIndex: number) => void;
  disabled?: boolean;
  className?: string;
}

const maskApiKey = (key: string): string => {
  if (!key || key.length < 8) return key || '未配置';
  return `${key.slice(0, 4)}...${key.slice(-4)}`;
};

export const PoolSelector: React.FC<PoolSelectorProps> = ({
  pools,
  selectedPool,
  onSelect,
  onAdd,
  onDelete,
  disabled = false,
  className = '',
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState<number | null>(null);

  const handleSelect = useCallback((poolIndex: number) => {
    onSelect(poolIndex);
    setIsOpen(false);
  }, [onSelect]);

  const handleDeleteClick = useCallback((e: React.MouseEvent, poolIndex: number) => {
    e.stopPropagation();
    setDeleteTarget(poolIndex);
  }, []);

  const handleDeleteConfirm = useCallback(() => {
    if (deleteTarget !== null) {
      onDelete(deleteTarget);
      setDeleteTarget(null);
    }
  }, [onDelete, deleteTarget]);

  const handleDeleteCancel = useCallback(() => {
    setDeleteTarget(null);
  }, []);

  const selectedOption = pools.find(p => p.poolIndex === selectedPool);

  return (
    <div className={clsx('relative', className)}>
      <label className="block text-sm font-medium text-gray-700 mb-2">
        选择池
      </label>
      
      {/* Dropdown Trigger */}
      <button
        type="button"
        onClick={() => !disabled && setIsOpen(!isOpen)}
        disabled={disabled}
        className={clsx(
          'w-full flex items-center justify-between px-4 py-3 border rounded-lg text-left transition-all',
          selectedOption ? 'bg-blue-50 border-blue-200' : 'bg-white border-gray-300',
          disabled ? 'opacity-50 cursor-not-allowed' : 'hover:border-gray-400 cursor-pointer',
          isOpen && 'ring-2 ring-blue-500 border-blue-500'
        )}
      >
        <div className="flex items-center gap-3">
          <div className={clsx(
            'w-3 h-3 rounded-full',
            selectedOption?.hasApiKey ? 'bg-green-400' : 'bg-gray-300'
          )} />
          <div>
            <div className="font-medium text-gray-900">
              {selectedOption ? `池 #${selectedOption.poolIndex}` : '请选择池'}
            </div>
            {selectedOption && (
              <div className="text-xs text-gray-500 mt-0.5">
                {selectedOption.modelCount} 模型 · 权重 {selectedOption.weight}
              </div>
            )}
          </div>
        </div>
        <div className="flex items-center gap-2">
          {selectedOption && (
            <StatusBadge variant="info" size="sm">
              {selectedOption.keySelectionMode === 'polling' ? '轮询' : '随机'}
            </StatusBadge>
          )}
          <svg
            className={clsx(
              'w-5 h-5 text-gray-400 transition-transform',
              isOpen && 'rotate-180'
            )}
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
          </svg>
        </div>
      </button>

      {/* Dropdown Menu */}
      {isOpen && (
        <>
          <div 
            className="fixed inset-0 z-10" 
            onClick={() => setIsOpen(false)}
          />
          <div className="absolute z-20 w-full mt-1 bg-white border border-gray-200 rounded-lg shadow-lg max-h-80 overflow-auto">
            {/* Add Button */}
            <button
              type="button"
              onClick={() => {
                onAdd();
                setIsOpen(false);
              }}
              className="w-full flex items-center gap-2 px-4 py-3 text-sm text-blue-600 hover:bg-blue-50 border-b border-gray-100 transition-colors"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
              </svg>
              添加新池
            </button>

            {/* Pool List */}
            {pools.length === 0 ? (
              <div className="px-4 py-8 text-center text-gray-500 text-sm">
                暂无池配置
              </div>
            ) : (
              <div className="py-1">
                {pools.map((pool) => (
                  <div
                    key={pool.poolIndex}
                    onClick={() => handleSelect(pool.poolIndex)}
                    className={clsx(
                      'group flex items-center justify-between px-4 py-3 cursor-pointer transition-colors',
                      selectedPool === pool.poolIndex ? 'bg-blue-50' : 'hover:bg-gray-50'
                    )}
                  >
                    <div className="flex items-center gap-3">
                      <div className={clsx(
                        'w-3 h-3 rounded-full',
                        pool.hasApiKey ? 'bg-green-400' : 'bg-gray-300'
                      )} />
                      <div>
                        <div className={clsx(
                          'font-medium',
                          selectedPool === pool.poolIndex ? 'text-blue-700' : 'text-gray-900'
                        )}>
                          池 #{pool.poolIndex}
                        </div>
                        <div className="text-xs text-gray-500">
                          {maskApiKey(pool.apiKeyPreview)} · {pool.modelCount} 模型
                        </div>
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      <StatusBadge variant="secondary" size="sm">
                        权重 {pool.weight}
                      </StatusBadge>
                      <StatusBadge variant="info" size="sm">
                        {pool.keySelectionMode === 'polling' ? '轮询' : '随机'}
                      </StatusBadge>
                      <button
                        type="button"
                        onClick={(e) => handleDeleteClick(e, pool.poolIndex)}
                        className="p-1 rounded transition-colors text-gray-300 hover:text-red-500 opacity-0 group-hover:opacity-100"
                        title="删除池"
                      >
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} 
                                d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                        </svg>
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </>
      )}

      <ConfirmDialog
        isOpen={deleteTarget !== null}
        title="确认删除"
        message={`确定要删除池 #${deleteTarget ?? ''} 吗？该池下的所有模型也将被删除，此操作无法撤销。`}
        confirmLabel="删除"
        cancelLabel="取消"
        variant="danger"
        onConfirm={handleDeleteConfirm}
        onCancel={handleDeleteCancel}
      />
    </div>
  );
};

export default PoolSelector;
